import { Component, computed, effect, inject, OnDestroy, signal, ChangeDetectionStrategy } from '@angular/core'
import { Router, ActivatedRoute } from '@angular/router'
import { Checkout, Depot, Payment } from 'depoto-core/src/entities'
import { NotificationService } from '../../services'
import { CoreService } from '../../services'
import { historyBack } from '../../utils'
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms'
import { CheckDataChanges } from '../../services/can-deactivate-guard.service'
import { TranslateService } from '@ngx-translate/core'
import { TitleService } from '../../services'
import { CheckoutService } from '../../services/entity-services/checkout.service'
import { DepotService } from '../../services/entity-services/depot.service'
import { PaymentService } from '../../services/entity-services/payment.service'
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental'
import { updateCachedListQueries } from '../../services/query-cache.service'

export const checkoutSchema = {
  id: null,
  name: null,
  amount: null,
  nextBillNumber: null,
  nextReservationNumber: null,
  billFooter: null,
  enabled: null,
  depots: {
    id: null,
    name: null,
    unavailablesUrl: null,
  },
  returnsDepot: {
    id: null,
    name: null,
    unavailablesUrl: null,
  },
  payments: {
    id: null,
    name: null,
    type: {
      id: null,
      name: null,
    },
  },
  currentClosing: {
    id: null,
    userOpen: {
      id: null,
      email: null,
      firstName: null,
      lastName: null,
      name: null,
      phone: null,
      groups: {
        id: null,
        name: null,
        roles: null,
      },
      roles: null,
      pin: null,
      enabled: null,
      checkouts: {
        id: null,
        name: null,
        depots: {
          id: null,
        },
      },
    },
    dateOpen: null,
    noteOpen: null,
    amountOpen: null,
    amountRealOpen: null,
    userClosed: {
      id: null,
      email: null,
      firstName: null,
      lastName: null,
      name: null,
      phone: null,
      groups: {
        id: null,
        name: null,
        roles: null,
      },
      roles: null,
      pin: null,
      enabled: null,
      checkouts: {
        id: null,
        name: null,
        depots: {
          id: null,
        },
      },
    },
    dateClosed: null,
    noteClosed: null,
    amountClosed: null,
    amountRealClosed: null,
    // orders: { // todo never ever
    vatAllocations: {
      vat: {
        id: null,
        name: null,
        percent: null,
        coefficient: null,
        default: null,
      },
      priceWithoutVat: null,
      vatPrice: null,
      price: null,
    },
    billUrl: null,
  },
  negativeReservation: null,
  autoPacking: null,
  autoPicking: null,
  autoDispatched: null,
  eventUrl: null,
  eventTypes: null,
}

@Component({
  selector: 'app-checkout',
  templateUrl: 'checkout.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CheckoutComponent implements CheckDataChanges, OnDestroy {
  private checkoutService = inject(CheckoutService)
  private depotService = inject(DepotService)
  private paymentService = inject(PaymentService)
  private queryClient = injectQueryClient()

  // Overridden in the constructor below and restored in ngOnDestroy - see there for why.
  private previousShouldReuseRoute: Router['routeReuseStrategy']['shouldReuseRoute']

  private initialFormValue = signal<any>(null)

  userHasRoleForCheckoutEdit = computed(() => this.core.services.user.user.roles.includes('ROLE_CHECKOUT_UPDATE'))

  submitted = signal(false)

  checkoutForm = new FormGroup({
    name: new FormControl({ value: '', disabled: false }, [Validators.required]),
    amount: new FormControl({ value: 0, disabled: false }, []),
    billFooter: new FormControl({ value: '', disabled: false }, []),
    nextReservationNumber: new FormControl({ value: 1, disabled: false }, []),
    nextBillNumber: new FormControl({ value: 1, disabled: false }, []),
    depots: new FormControl({ value: [], disabled: false }, []),
    payments: new FormControl({ value: [], disabled: false }, []),
    returnsDepot: new FormControl({ value: 0, disabled: false }, []),
    eventTypes: new FormControl({ value: [], disabled: false }, []),
    eventUrl: new FormControl({ value: '', disabled: false }, []),
    negativeReservation: new FormControl({ value: false, disabled: false }, []),
    autoPicking: new FormControl({ value: false, disabled: false }, []),
    autoPacking: new FormControl({ value: false, disabled: false }, []),
    autoDispatched: new FormControl({ value: false, disabled: false }, []),
    enabled: new FormControl({ value: true, disabled: false }, []),
  })

  readonly queryDepotsOptions = injectQuery(() => ({
    queryKey: ['depots', 'options'],
    queryFn: async () => {
      if (this.core.services.user.hasRole('ROLE_SALESMAN') && !this.core.services.user.user?.depots?.length) {
        // TODO CORE USER
        return []
      } else {
        return await this.depotService
          .getDepots(
            {
              sort: 'name',
              direction: 'asc',
            },
            { id: null, name: null, enabled: null }
          )
          .then(result => result.items)
      }
    },
    select: data => {
      if (
        !this.depotService.doesUserHaveAllDepots(data as any[]) &&
        this.core.services.user.user?.depots?.length // TODO CORE USER
      ) {
        return this.core.services.user.user.depots // TODO CORE USER
      } else {
        return data as any[]
      }
    },
  }))

  readonly queryEventTypes = injectQuery(() => ({
    queryKey: ['eventTypes', 'options'],
    queryFn: async () => {
      return await this.checkoutService.getEventTypes().then(result => {
        return result.items
      })
    },
  }))

  readonly queryPayments = injectQuery(() => ({
    queryKey: ['payments', 'options'],
    queryFn: async () => {
      return await this.paymentService.getPayments().then(result => {
        return result.items
      })
    },
  }))

  readonly queryCheckout = injectQuery(() => ({
    queryKey: ['checkout', this.route.snapshot.params['id']],
    queryFn: async () => {
      const id = this.route.snapshot.params['id']
      if (id) {
        const a = await this.checkoutService.getCheckout(id, checkoutSchema)
        if (!a?.id) {
          // getById() defaults to throwErrors: false, so a GraphQL error (e.g. 403 after
          // switching to an account without access to this checkout) resolves to an empty
          // entity instead of rejecting - see depoto-core's BaseEntityService.getSingleEntity().
          // Treat a missing id as a failed load so the query actually errors instead of
          // silently rendering a blank form on the same, now-forbidden, URL.
          throw new Error(`Checkout ${id} failed to load`)
        }
        return a
      } else {
        this.setNextBillNumber.set(true)
        this.setNextReservationNumber.set(true)
        return new Checkout()
      }
    },
    // NB: onSuccess/onError are not supported on plain queries in TanStack Query v5 (mutation-only) -
    // they're silently ignored here. Real side effects for load success/failure run from the
    // effect() in the constructor below, which reacts to queryCheckout.data()/isError().
  }))

  createCheckout = injectMutation(() => ({
    mutationFn: () => {
      this.submitted.set(true)
      if (this.checkoutForm.invalid) {
        throw new Error('Form is invalid')
      }
      const n = this.getDataToSave()

      return this.checkoutService.createCheckout(n, checkoutSchema)
    },
    onSuccess: data => {
      this.submitted.set(false)
      // After successful create, snapshot current form as the new baseline to avoid false unsaved prompts
      const current = this.checkoutForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.checkoutForm.markAsPristine()
      this.notificationService.success(this.translateService.instant('alert.checkout.created'))
      // Update detail cache for this checkout
      this.queryClient.setQueryData(['checkout', data.id], data)

      updateCachedListQueries(this.queryClient, ['checkouts', 'options'], data, {
        mode: 'insert',
        insertPosition: 'start',
      })

      this.removeCheckoutListings()

      this.router.navigate(['/checkout/', data['id']], { replaceUrl: true })
    },
  }))

  updateCheckout = injectMutation(() => ({
    mutationFn: () => {
      this.submitted.set(true)
      if (this.checkoutForm.invalid) {
        throw new Error('Form is invalid')
      }
      const checkoutDataToSave = this.getDataToSave()
      return this.checkoutService.updateCheckout(checkoutDataToSave, checkoutSchema)
    },
    onSuccess: async data => {
      this.submitted.set(false)
      this.core.services.storage.getSync(this.core.services.storage.keys.auth.company)
      const id = Number(this.route.snapshot.params['id'])
      if (this.setNextBillNumber() && id) {
        await this.checkoutService.setNextBillNumber({
          id,
          nextBillNumber: this.checkoutForm.get('nextBillNumber')?.value,
        })
      }
      if (this.setNextReservationNumber() && id) {
        await this.checkoutService.setNextReservationNumber({
          id,
          nextReservationNumber: this.checkoutForm.get('nextReservationNumber')?.value,
        })
      }
      // Snapshot current form as baseline and mark pristine so guard won't prompt right after save
      const current = this.checkoutForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.checkoutForm.markAsPristine()
      this.notificationService.success(this.translateService.instant('alert.checkout.updated'))
      // Update detail cache for this checkout
      this.queryClient.setQueryData(['checkout', data.id], data)

      updateCachedListQueries(this.queryClient, ['checkouts', 'options'], data, {
        mode: 'insert',
        insertPosition: 'start',
      })

      this.removeCheckoutListings()

      this.router.navigate(['/checkout', data['id']])
    },
  }))

  deleteCheckout = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)
      const id = Number(this.route.snapshot.params['id'])
      await this.checkoutService.deleteCheckout({ id }).then(data => {
        this.notificationService.success(this.translateService.instant('alert.checkout.deleted'))
        const idStr = String(this.route.snapshot.params['id'])
        // const idStr = String(this.router.url.split('/')[2])

        // OPTIONS: keep as-is → update options cache by removing the tag
        updateCachedListQueries(this.queryClient, ['checkouts', 'options'], { id: Number(idStr) } as any, {
          mode: 'delete',
        })

        // LISTING ['tags', sortProp, sortDir]: On DELETE → if id is present in cached pages, remove it; otherwise do nothing
        this.removeFromListingsIfPresent(Number(idStr))
      })
    },
    onSuccess: data => {
      this.submitted.set(false)
      // Prune deleted checkout from any cached lists (supports infinite queries and arrays)
      this.router.navigate(['/checkouts'], { replaceUrl: true })
    },
  }))

  isCreating = computed(() => {
    return !this.route.snapshot.params['id']
  })
  setNextBillNumber = signal(false)
  setNextReservationNumber = signal(false)
  loading = computed(() => {
    return (
      this.queryCheckout.isFetching() ||
      this.queryCheckout.isPending() ||
      this.queryDepotsOptions.isFetching() ||
      this.queryDepotsOptions.isPending() ||
      this.queryPayments.isFetching() ||
      this.queryPayments.isPending() ||
      this.queryEventTypes.isFetching() ||
      this.queryEventTypes.isPending()
    )
  })

  depots = computed(() => {
    // TanStack query's data() may be undefined on first run or data may not be a function if query isn't ready yet
    const dataFn = (this.queryDepotsOptions as any)?.data
    const raw = typeof dataFn === 'function' ? dataFn() : undefined
    return Array.isArray(raw) ? raw : []
  })
  // depotsForReturns = computed(() => {
  //   return [
  //     { id: 0, name: 'Žádný', unavailablesUrl: '', checkouts: [], users: [] },
  //     ...this.depots(),
  //   ]
  // })

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private core: CoreService,
    private titleService: TitleService
  ) {
    effect(() => {
      const ch = this.queryCheckout.data()
      const id = this.route.snapshot.params['id']
      if (id && ch) {
        this.titleService.updateTitleWithObject(ch['id'], ch['name'], 'title.checkout-id')
        this.core.services.storage.getSync(this.core.services.storage.keys.auth.company)
        this.fillFormValue(ch)
        this.setNextBillNumber.set(false)
        this.setNextReservationNumber.set(false)
      }
      if (id && this.queryCheckout.isError()) {
        // Loading this checkout failed (e.g. 403 after switching to an account without access
        // to it) - navigate away instead of staying on the now-forbidden id.
        this.router.navigate(['/checkouts'], { replaceUrl: true })
      }
    })
    // Forces this route to reload fresh (rather than being reused) when navigating between
    // different checkout ids. This mutates the app-wide Router's routeReuseStrategy though, not
    // something scoped to this component - left in place it silently breaks route reuse (e.g.
    // wizard-style child routes elsewhere, like order creation) for the rest of the session. Put
    // the original back on the way out.
    this.previousShouldReuseRoute = this.router.routeReuseStrategy.shouldReuseRoute.bind(this.router.routeReuseStrategy)
    this.router.routeReuseStrategy.shouldReuseRoute = () => false
  }

  ngOnDestroy(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = this.previousShouldReuseRoute
  }

  goBack(): void {
    historyBack()
  }

  public fillFormValue(checkout) {
    const value = {
      name: checkout.name ?? '',
      amount: checkout.amount ?? 0,
      billFooter: checkout.billFooter ?? '',
      nextReservationNumber: checkout.nextReservationNumber ?? 0,
      nextBillNumber: checkout.nextBillNumber ?? 0,
      depots: checkout.depots ?? [],
      payments: checkout.payments ?? [],
      returnsDepot: checkout.returnsDepot ?? 0,
      eventTypes: checkout.eventTypes ?? [],
      eventUrl: checkout.eventUrl ?? '',
      negativeReservation: checkout.negativeReservation ?? false,
      autoPicking: checkout.autoPicking ?? false,
      autoPacking: checkout.autoPacking ?? false,
      autoDispatched: checkout.autoDispatched ?? false,
      enabled: checkout.enabled ?? false,
    }

    this.checkoutForm.setValue(value)
    // Store initial values snapshot and reset form dirty state
    this.initialFormValue.set(structuredClone(value))
    this.checkoutForm.markAsPristine()
  }

  hasUnsavedDataChanges(): boolean {
    const current = this.checkoutForm.getRawValue() as any
    const initial = this.initialFormValue() as any

    // If initial is not yet set (e.g., brand-new form), fall back to form dirty flag
    if (!initial) {
      return this.checkoutForm.dirty
    }

    const norm = (v: any) => (v === undefined || v === null ? '' : v)

    const idArray = (arr: any[]) =>
      Array.isArray(arr) ? arr.map((x: any) => x?.id).filter((v: any) => v !== undefined && v !== null) : []
    const eqArrayIds = (a: any[], b: any[]) => {
      const aa = idArray(a)
        .slice()
        .sort((x, y) => (x ?? 0) - (y ?? 0))
      const bb = idArray(b)
        .slice()
        .sort((x, y) => (x ?? 0) - (y ?? 0))
      if (aa.length !== bb.length) return false
      for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false
      return true
    }
    const eqArrayStrings = (a: any[], b: any[]) => {
      const aa = Array.isArray(a) ? a.map(String).slice().sort() : []
      const bb = Array.isArray(b) ? b.map(String).slice().sort() : []
      if (aa.length !== bb.length) return false
      for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false
      return true
    }

    const changed =
      norm(String(current?.name)) !== norm(String(initial?.name)) ||
      norm(Number(current?.amount)) !== norm(Number(initial?.amount)) ||
      norm(String(current?.billFooter)) !== norm(String(initial?.billFooter)) ||
      norm(Number(current?.nextReservationNumber)) !== norm(Number(initial?.nextReservationNumber)) ||
      norm(Number(current?.nextBillNumber)) !== norm(Number(initial?.nextBillNumber)) ||
      // norm(Number(current?.returnsDepot)) !== norm(Number(initial?.returnsDepot)) || todo
      norm(String(current?.eventUrl)) !== norm(String(initial?.eventUrl)) ||
      norm(!!current?.negativeReservation) !== norm(!!initial?.negativeReservation) ||
      norm(!!current?.autoPicking) !== norm(!!initial?.autoPicking) ||
      norm(!!current?.autoPacking) !== norm(!!initial?.autoPacking) ||
      norm(!!current?.autoDispatched) !== norm(!!initial?.autoDispatched) ||
      norm(!!current?.enabled) !== norm(!!initial?.enabled) ||
      !eqArrayIds(current?.depots, initial?.depots) ||
      !eqArrayStrings(current?.payments, initial?.payments) ||
      !eqArrayIds(current?.eventTypes, initial?.eventTypes)

    return changed
  }

  toggleDepot(depot: Depot, event: any) {
    if (event.target.checked) {
      this.checkoutForm.value.depots.push(depot)
    } else {
      this.checkoutForm.value.depots.forEach((d: Depot, i: number) => {
        if (depot.id === d.id && depot.name === d.name) {
          this.checkoutForm.value.depots.splice(i, 1)
        }
      })
    }
  }

  isChosenDepot(depot: Depot): boolean {
    let exists = false
    this.checkoutForm.value.depots.forEach((d: Depot) => {
      if (d.id === depot.id && d.name === depot.name) {
        exists = true
        return
      }
    })
    return exists
  }

  togglePayment(payment: Payment, event: any) {
    if (event.target.checked) {
      this.checkoutForm.value.payments.push(payment)
    } else {
      this.checkoutForm.value.payments.forEach((p: Payment, i: number) => {
        if (payment.id === p.id && payment.name === p.name) {
          this.checkoutForm.value.payments.splice(i, 1)
        }
      })
    }
  }

  isChosenPayment(payment: Payment): boolean {
    let exists = false
    this.checkoutForm.value.payments.forEach((p: Payment) => {
      if (p.id === payment.id && p.name === payment.name) {
        exists = true
        return
      }
    })
    return exists
  }

  isEventTypeSelected(eventType: string): boolean {
    return this.checkoutForm.value.eventTypes.includes(eventType)
  }

  toggleEventType(eventChecked: boolean, eventType: string): void {
    let eventTypes = this.checkoutForm.get('eventTypes').value
    if (eventChecked) {
      eventTypes.push(eventType)
    } else {
      eventTypes = eventTypes.filter(type => type !== eventType)
    }
    this.checkoutForm.get('eventTypes').setValue(eventTypes)
  }

  getDataToSave() {
    const current = this.checkoutForm.getRawValue()
    const initial = this.initialFormValue()
    const changed: any = {}

    // Include id when editing
    const routeId = this.route.snapshot.params['id']
    if (routeId) changed.id = Number(routeId)

    // Always include required field(s)
    changed.name = current.name

    // Helper normalizers
    const norm = (v: any) => (v === undefined || v === null ? '' : v)
    const eqArray = (a: any[], b: any[]) => {
      if (!Array.isArray(a) && !Array.isArray(b)) return true
      if (!Array.isArray(a) || !Array.isArray(b)) return false
      if (a.length !== b.length) return false
      for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false
      }
      return true
    }

    // amount (number)
    if (norm(current.amount) !== norm(initial?.amount)) {
      changed.amount = current.amount
    }

    // next numbers (only if toggles are on)
    if (this.setNextBillNumber()) {
      if (norm(current.nextBillNumber) !== norm(initial?.nextBillNumber)) {
        changed.nextBillNumber = current.nextBillNumber
      }
    }
    if (this.setNextReservationNumber()) {
      if (norm(current.nextReservationNumber) !== norm(initial?.nextReservationNumber)) {
        changed.nextReservationNumber = current.nextReservationNumber
      }
    }

    // depots (array of objects -> compare by id array)
    const currentDepotIds: number[] = Array.isArray(current.depots)
      ? current.depots.map((d: any) => d?.id).filter((v: any) => v !== undefined && v !== null)
      : []
    const initialDepotIds: number[] = Array.isArray(initial?.depots)
      ? initial.depots.map((d: any) => d?.id).filter((v: any) => v !== undefined && v !== null)
      : []
    if (!eqArray(currentDepotIds, initialDepotIds)) {
      changed.depots = current.depots
    }

    // returnsDepot (single id)
    const currentReturnsDepotId = current?.returnsDepot ?? 0
    const initialReturnsDepotId = initial?.returnsDepot ?? initial?.returnsDepot?.id ?? 0
    if (currentReturnsDepotId !== initialReturnsDepotId) {
      changed.returnsDepot = { id: currentReturnsDepotId }
    }

    // eventTypes (array of strings)
    const currentEvents: string[] = Array.isArray(current.eventTypes) ? current.eventTypes.slice() : []
    const initialEvents: string[] = Array.isArray(initial?.eventTypes) ? initial.eventTypes.slice() : []
    if (!eqArray(currentEvents, initialEvents)) {
      changed.eventTypes = currentEvents
    }

    // eventUrl (string)
    if (norm(current.eventUrl) !== norm(initial?.eventUrl)) {
      changed.eventUrl = current.eventUrl
    }

    // billFooter (string)
    if (norm(current.billFooter) !== norm(initial?.billFooter)) {
      changed.billFooter = current.billFooter
    }

    // booleans
    if (norm(current.negativeReservation) !== norm(initial?.negativeReservation)) {
      changed.negativeReservation = current.negativeReservation
    }
    if (norm(current.autoPicking) !== norm(initial?.autoPicking)) {
      changed.autoPicking = current.autoPicking
    }
    if (norm(current.autoPacking) !== norm(initial?.autoPacking)) {
      changed.autoPacking = current.autoPacking
    }
    if (norm(current.autoDispatched) !== norm(initial?.autoDispatched)) {
      changed.autoDispatched = current.autoDispatched
    }
    if (norm(current.enabled) !== norm(initial?.enabled)) {
      changed.enabled = current.enabled
    }

    // payments are edited outside the form (this.checkout.payments)
    // Include when they differ from initial (if initial snapshot had payments), otherwise include if present.
    const currentPaymentIds: any[] = Array.isArray(this.checkoutForm.value?.payments)
      ? this.checkoutForm.value.payments.map(p => p?.id)
      : []
    const initialPaymentIds: any[] = Array.isArray(initial?.payments) ? initial.payments.map((p: any) => p?.id) : []
    const paymentsChanged = !eqArray(currentPaymentIds, initialPaymentIds)
    if (paymentsChanged && Array.isArray(this.checkoutForm.value?.payments)) {
      changed.payments = this.checkoutForm.value.payments
    }

    return changed
  }

  setNextReservationNumberChanged(event) {
    this.setNextReservationNumber.set(event)
  }

  setNextBillNumberChanged(event) {
    this.setNextBillNumber.set(event)
  }

  private removeCheckoutListings(): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['checkouts'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      this.queryClient.removeQueries({ queryKey: key as any, exact: true })
    }
  }

  private isCheckoutsListingKey(key: unknown[]): boolean {
    if (!Array.isArray(key)) return false
    if (key.length !== 3) return false
    return key[0] === 'checkouts' && typeof key[1] === 'string' && typeof key[2] === 'string'
  }

  private listingContainsId(cached: any, id: number | string): boolean {
    const idStr = String(id)
    if (!cached || !Array.isArray(cached.pages)) return false
    for (const page of cached.pages) {
      const items = Array.isArray(page) ? page : Array.isArray(page?.items) ? page.items : undefined
      if (!Array.isArray(items)) {
        continue
      }
      if (items.some((it: any) => String(it?.id) === idStr)) {
        return true
      }
    }
    return false
  }

  private removeIdFromCachedListing(key: unknown[], id: number | string): void {
    const cached: any = this.queryClient.getQueryData(key as any)
    if (!cached || !Array.isArray(cached.pages)) return
    const idStr = String(id)
    const newPages = cached.pages.map((page: any) => {
      if (Array.isArray(page)) {
        return page.filter((it: any) => String(it?.id) !== idStr)
      }
      if (Array.isArray(page?.items)) {
        return { ...page, items: page.items.filter((it: any) => String(it?.id) !== idStr) }
      }
      return page
    })
    this.queryClient.setQueryData(key as any, { ...cached, pages: newPages })
  }

  private removeFromListingsIfPresent(id: number | string): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['checkouts'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      if (!this.isCheckoutsListingKey(key as unknown[])) continue
      if (!this.listingContainsId(cached, id)) continue
      this.removeIdFromCachedListing(key as unknown[], id)
    }
  }

  get formControls(): { [key: string]: AbstractControl } {
    return this.checkoutForm.controls
  }
}
